import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import styled from "styled-components";
import Button from "@ui/button/Button";
import { GD } from "../GD";
import { canAnalyzeTransactionFailure } from "../permissions";
import { collectFailureAnalysisData, requestFailureAnalysis, type FailureAnalysisModel, type FailureAnalysisLanguage } from "../services/transactionFailureAnalysis";
import { VALERA_AUTH } from "../services/valeraAuth";
import type { IPaymentSession } from "../types/types";
import { TransactionFailureAnalysisResult } from "./TransactionFailureAnalysisResult";

const LANGUAGE_STORAGE_KEY = "transactionFailureAnalysis.lang";

export function useTransactionFailureAnalysis(transaction: IPaymentSession | null, projectId: number | undefined, userId: number | undefined, ready: boolean) {
	useLocale();
	const [model, setModel] = useState<FailureAnalysisModel>("gpt-6.1-sol");
	const [lang, setLanguage] = useState<FailureAnalysisLanguage>(() => {
		try { return localStorage.getItem(LANGUAGE_STORAGE_KEY) === "ru" ? "ru" : "en"; }
		catch { return "en"; }
	});
	const setLang = (value: FailureAnalysisLanguage) => {
		setLanguage(value);
		try { localStorage.setItem(LANGUAGE_STORAGE_KEY, value); }
		catch { /* Keep the selection for this popup when browser storage is unavailable. */ }
	};
	const [prompt, setPrompt] = useState("");
	const [formExpanded, setFormExpanded] = useState(true);
	const [progress, setProgress] = useState("");
	const [startedAt, setStartedAt] = useState<number | null>(null);
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	useEffect(() => {
		if (startedAt === null) return;
		const update = () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
		update();
		const timer = setInterval(update, 1000);
		return () => clearInterval(timer);
	}, [startedAt]);
	const [error, setError] = useState("");
	const [result, setResult] = useState<Awaited<ReturnType<typeof requestFailureAnalysis>> | null>(null);
	const controller = useRef<AbortController | null>(null);
	const allowed = !!transaction && !transaction._is_release && canAnalyzeTransactionFailure(userId, transaction.status);
	useEffect(() => {
		controller.current?.abort();
		controller.current = null;
		setModel("gpt-6.1-sol"); setPrompt(""); setProgress(""); setError(""); setResult(null);
		setFormExpanded(true);
		setStartedAt(null); setElapsedSeconds(0);
		return () => { controller.current?.abort(); controller.current = null; };
	}, [transaction?.id, projectId, userId, allowed]);
	const cancel = () => { controller.current?.abort(); controller.current = null; setProgress(""); setStartedAt(null); };
	const run = async () => {
		if (!allowed || !ready || !transaction || projectId === undefined || controller.current) return;
		const active = new AbortController(); controller.current = active;
		setError(""); setResult(null); setProgress(msg("collectingTransactionData"));
		setElapsedSeconds(0); setStartedAt(Date.now());
		try {
			const data = await collectFailureAnalysisData(transaction, request => GD.REQ_API.request(request), active.signal, projectId, setProgress);
			active.signal.throwIfAborted();
			setProgress(msg("analyzingTransactionThisMayTakeMoreThanAMinute"));
			const analysis = await requestFailureAnalysis(data, model, prompt, active.signal, VALERA_AUTH, lang);
			if (!active.signal.aborted) { setResult(analysis); setFormExpanded(false); }
		} catch (reason) {
			if (!active.signal.aborted) setError(reason instanceof Error ? reason.message : msg("couldNotAnalyzeThisTransaction"));
		} finally {
			if (controller.current === active) { controller.current = null; setProgress(""); setStartedAt(null); }
		}
	};
	return { model, setModel, lang, setLang, prompt, setPrompt, formExpanded, setFormExpanded, progress, elapsedSeconds, error, result, run, cancel, ready: allowed && ready };
}

const Card = styled.section`
	background: #fff; border: 1px solid #d0d5dd; border-radius: 12px; padding: 20px;
	box-shadow: 0 1px 2px rgba(16,24,40,.05); color: #344054; font-size: 13px; line-height: 1.5;
	display: flex; flex-direction: column;
	h3 { margin: 0; color: #182230; font-size: 16px; font-weight: 600; }
	p { margin: 0; color: #667085; }
	label { display: flex; flex-direction: column; gap: 6px; font-weight: 500; }
	select, textarea { box-sizing: border-box; width: 100%; border: 1px solid #d0d5dd; border-radius: 8px; padding: 10px 12px; color: #344054; background: #fff; font: inherit; }
	textarea { resize: vertical; min-height: 88px; }
	select:focus-visible, textarea:focus-visible { outline: 2px solid #167ca6; outline-offset: 2px; }
`;

const FormToggle = styled.button<{ $expanded: boolean }>`
	display: flex; align-items: center; justify-content: space-between; gap: 12px;
	width: 100%; border: 0; padding: 0; background: none; color: #182230; font: inherit;
	font-size: 16px; font-weight: 600; text-align: left; cursor: pointer;
	span { display: inline-flex; align-items: center; gap: 6px; color: #667085; font-size: 12px; font-weight: 400; }
	svg { transform: rotate(${({ $expanded }) => $expanded ? "180deg" : "0deg"}); transition: transform 250ms ease; }
	&:focus-visible { outline: 2px solid #167ca6; outline-offset: 5px; border-radius: 3px; }
	@media (prefers-reduced-motion: reduce) { svg { transition: none; } }
`;
const FormCollapse = styled.div<{ $expanded: boolean }>`
	display: grid; grid-template-rows: ${({ $expanded }) => $expanded ? "1fr" : "0fr"};
	opacity: ${({ $expanded }) => $expanded ? 1 : 0};
	visibility: ${({ $expanded }) => $expanded ? "visible" : "hidden"};
	transition: grid-template-rows 300ms ease, opacity 200ms ease, visibility 300ms;
	> div { min-height: 0; overflow: hidden; }
	@media (prefers-reduced-motion: reduce) { transition: none; }
`;

export function TransactionFailureAnalysis({ analysis }: { analysis: ReturnType<typeof useTransactionFailureAnalysis> }) {
	useLocale();
	const busy = !!analysis.progress;
	const formId = useId();
	const toggleRef = useRef<HTMLButtonElement>(null);
	const formRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!analysis.formExpanded && formRef.current?.contains(document.activeElement)) toggleRef.current?.focus();
	}, [analysis.formExpanded]);
	return <div style={{ display: "flex", flexDirection: "column", gap: 20 }} aria-busy={busy}>
		<Card>
		<FormToggle ref={toggleRef} type="button" $expanded={analysis.formExpanded} aria-expanded={analysis.formExpanded} aria-controls={formId} onClick={() => analysis.setFormExpanded(!analysis.formExpanded)}>{msg("failureAnalysis")}<span>{analysis.formExpanded ? msg("hideRequest") : msg("editRequest")}<ChevronDown size={16} /></span>
		</FormToggle>
		<FormCollapse ref={formRef} id={formId} $expanded={analysis.formExpanded} aria-hidden={!analysis.formExpanded}>
		<div><div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 16 }}>
		<p>{msg("collectTransactionDetailsAndAllAvailableProcessingLogPagesTo_edcb216f")}</p>
		<label>{msg("model")}<select value={analysis.model} disabled={busy} onChange={event => analysis.setModel(event.target.value as FailureAnalysisModel)}>
				<option value="gpt-6.1-sol">{msg("gpt61SolEconomical")}</option>
				<option value="gpt-6-astra">{msg("gpt6AstraPremiumHigherCost")}</option>
			</select>
		</label>
		<label>{msg("responseLanguage")}<select value={analysis.lang} disabled={busy} onChange={event => analysis.setLang(event.target.value as FailureAnalysisLanguage)}>
				<option value="en">{msg("english")}</option>
				<option value="ru">{msg("message_6da140fd")}</option>
			</select>
		</label>
		<label>{msg("questionOptional")}<textarea value={analysis.prompt} disabled={busy} onChange={event => analysis.setPrompt(event.target.value)} placeholder={msg("whatCausedThisTransactionToFail")} />
		</label>
		<div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
			<Button variant="primarySmall" disabled={busy || !analysis.ready} onClick={analysis.run} title={analysis.result ? msg("analyzeAgain") : msg("analyzeTransaction")} />
			{busy && <Button variant="secondarySmall" onClick={analysis.cancel} title={msg("cancel")} />}
		</div>
		</div></div>
		</FormCollapse>
		{busy && <div style={{ marginTop: 16 }}>
			<p role="status">{analysis.progress}</p>
			<p role="timer" style={{ marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{msg("elapsed")}{Math.floor(analysis.elapsedSeconds / 60)}:{String(analysis.elapsedSeconds % 60).padStart(2, "0")}</p>
		</div>}
		{analysis.error && <div role="alert" style={{ color: "#b42318", marginTop: 16 }}>{analysis.error}</div>}
		</Card>
		{analysis.result && <TransactionFailureAnalysisResult result={analysis.result} />}
	</div>;
}
